import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Button,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Home,
} from '@mui/icons-material';
import { Unit } from '../services/api';

interface HierarchicalUnitTreeProps {
  units: Unit[];
  onAddUnit: (parentUnit?: Unit) => void;
  onEditUnit: (unit: Unit) => void;
  onDeleteUnit: (unit: Unit) => void;
  onViewUnit: (unit: Unit) => void;
  isAdmin: boolean;
  level?: number;
}

interface UnitNodeProps {
  unit: Unit;
  onAddUnit: (parentUnit?: Unit) => void;
  onEditUnit: (unit: Unit) => void;
  onDeleteUnit: (unit: Unit) => void;
  onViewUnit: (unit: Unit) => void;
  isAdmin: boolean;
  level: number;
}

const UnitNode: React.FC<UnitNodeProps> = ({
  unit,
  onAddUnit,
  onEditUnit,
  onDeleteUnit,
  onViewUnit,
  isAdmin,
  level,
}) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = unit.descendants && unit.descendants.length > 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'vacant':
        return 'success';
      case 'occupied':
        return 'primary';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  const indentLevel = level * 20;

  return (
    <Box>
      {/* Unit Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1,
          px: 2,
          ml: `${indentLevel}px`,
          borderLeft: level > 0 ? '1px solid #e0e0e0' : 'none',
          borderBottom: '1px solid #f5f5f5',
          backgroundColor: level % 2 === 0 ? '#fafafa' : '#ffffff',
          '&:hover': {
            backgroundColor: '#f0f0f0',
          },
        }}
      >
        {/* Expand/Collapse Button */}
        <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          ) : (
            <Box sx={{ width: 24 }} />
          )}
        </Box>

        {/* Unit Icon */}
        <Home sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />

        {/* Unit Info */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {unit.unit_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
          <Typography variant="body2">
            {unit.name}
          </Typography>
          <Chip
            label={unit.type}
            size="small"
            variant="outlined"
            sx={{ ml: 1 }}
          />
          <Chip
            label={unit.status}
            size="small"
            color={getStatusColor(unit.status) as any}
            sx={{ ml: 0.5 }}
          />
        </Box>

        {/* Unit Details */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {unit.bedrooms}BR / {unit.bathrooms}BA
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ${unit.rent_amount}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Unit">
            <IconButton
              size="small"
              color="info"
              onClick={() => onViewUnit(unit)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <>
              <Tooltip title="Add Sub-Unit">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onAddUnit(unit)}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Unit">
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => onEditUnit(unit)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Unit">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDeleteUnit(unit)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* Children */}
      {hasChildren && (
        <Collapse in={expanded}>
          <Box>
            {unit.descendants!.map((childUnit) => (
              <UnitNode
                key={childUnit.id}
                unit={childUnit}
                onAddUnit={onAddUnit}
                onEditUnit={onEditUnit}
                onDeleteUnit={onDeleteUnit}
                onViewUnit={onViewUnit}
                isAdmin={isAdmin}
                level={level + 1}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const HierarchicalUnitTree: React.FC<HierarchicalUnitTreeProps> = ({
  units,
  onAddUnit,
  onEditUnit,
  onDeleteUnit,
  onViewUnit,
  isAdmin,
  level = 0,
}) => {
  if (!units || units.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          color: 'text.secondary',
        }}
      >
        <Home sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No units found
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          This property doesn't have any units yet.
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => onAddUnit()}
          >
            Add First Unit
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Typography variant="h6">
          Units Structure
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddUnit()}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            Add Root Unit
          </Button>
        )}
      </Box>

      {/* Tree Content */}
      <Box>
        {units.map((unit) => (
          <UnitNode
            key={unit.id}
            unit={unit}
            onAddUnit={onAddUnit}
            onEditUnit={onEditUnit}
            onDeleteUnit={onDeleteUnit}
            onViewUnit={onViewUnit}
            isAdmin={isAdmin}
            level={0}
          />
        ))}
      </Box>
    </Box>
  );
};

export default HierarchicalUnitTree;
